import React, { useCallback, useEffect, useState } from "react";

import Form from "react-bootstrap/Form";
import { useForm } from "react-hook-form";

import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";
import CsHistoryList from "@/components/CsHistoryList";
import Modal from "@/components/Modal";
import { loadCsHistoryByDelivery } from "@/lib/csHistory";

import {
  useCsesStore,
  useDeliveriesStore,
  useLoadingStore,
  useModalStore,
} from "@/stores/hooks";

const Title = styled.h4`
  margin: 0;
`;

const FormGroup = styled(Form.Group)`
  margin-bottom: 12px;
`;

const FormLabel = styled(Form.Label)`
  font-weight: 500;
  margin-bottom: 6px;
`;

const ReadOnly = styled.div`
  padding: 6px 0;
`;

const BookIdRow = styled.div`
  display: flex;
  align-items: center;
`;

const LookupButton = styled(Button)`
  margin-left: 8px;
  flex-shrink: 0;
`;

const DeliveryInfo = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  background: #f7f7f7;
  font-size: 13px;
`;

const ButtonWrap = styled.div`
  margin-top: 16px;
  text-align: right;
`;

const ModalButton = styled(Button)`
  margin-left: 5px;
`;

const HistorySection = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eee;
`;

const HistoryTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 10px;
`;

const HistoryWrap = styled.div`
  max-height: 320px;
  overflow-y: auto;
`;

export default function CsFormModal({
  cs,
  bookId = "",
  delivery: presetDelivery,
  onAfterSubmit,
}) {
  const { closeModal } = useModalStore();
  const { startLoading, finishLoading } = useLoadingStore();
  const { create, update, fetchByBookId } = useCsesStore();
  const { fetchByIdOnTheFly } = useDeliveriesStore();
  const { errors, handleSubmit, register, reset, watch, setValue } = useForm();
  const [verifiedDelivery, setVerifiedDelivery] = useState(null);
  const [previousCses, setPreviousCses] = useState([]);

  const isEdit = Boolean(cs?.id);
  const canEdit = !isEdit || cs.canCorpEdit();
  const watchedBookId = watch("bookId");
  const watchedText = watch("text");
  const hasText = Boolean((watchedText || "").trim());

  const loadPreviousCses = useCallback(
    async ({ targetBookId, delivery } = {}) => {
      const lookupBookId = targetBookId || delivery?.bookId;

      if (!lookupBookId) {
        setPreviousCses([]);
        return;
      }

      const { rows } = await loadCsHistoryByDelivery({
        bookId: lookupBookId,
        delivery,
        fetchByIdOnTheFly,
        fetchByBookId,
      });

      setPreviousCses(
        (rows || []).filter((item) => !cs?.id || item.id !== cs.id),
      );
    },
    [cs, fetchByBookId, fetchByIdOnTheFly],
  );

  useEffect(() => {
    reset({
      bookId: cs?.renderBookId?.() || cs?.bookId || bookId || "",
      text: cs?.text || "",
    });
  }, [bookId, cs, reset]);

  useEffect(() => {
    if (isEdit || !bookId) {
      return;
    }

    let cancelled = false;

    const lookupPreset = async () => {
      try {
        startLoading();
        const delivery =
          presetDelivery?.bookId === bookId
            ? presetDelivery
            : await fetchByIdOnTheFly(bookId);

        if (cancelled) {
          return;
        }

        if (delivery?.bookId) {
          setVerifiedDelivery(delivery);
          setValue("bookId", delivery.bookId);

          if (!cancelled) {
            await loadPreviousCses({
              targetBookId: delivery.bookId,
              delivery,
            });
          }
        }
      } catch (_e) {
        if (!cancelled) {
          setVerifiedDelivery(null);
          setPreviousCses([]);
        }
      }

      finishLoading();
    };

    lookupPreset();

    return () => {
      cancelled = true;
    };
  }, [
    bookId,
    fetchByIdOnTheFly,
    finishLoading,
    isEdit,
    loadPreviousCses,
    presetDelivery,
    setValue,
    startLoading,
  ]);

  useEffect(() => {
    if (isEdit) {
      return;
    }

    const current = (watchedBookId || "").trim();
    const verified = verifiedDelivery?.bookId || "";

    if (verified && current !== verified) {
      setVerifiedDelivery(null);
      setPreviousCses([]);
    }
  }, [isEdit, verifiedDelivery, watchedBookId]);

  useEffect(() => {
    if (!isEdit) {
      return;
    }

    const editBookId = cs?.renderBookId?.() || cs?.bookId;

    if (!editBookId) {
      return;
    }

    let cancelled = false;

    const loadForEdit = async () => {
      try {
        startLoading();
        await loadPreviousCses({ targetBookId: editBookId });
      } catch (_e) {
        if (!cancelled) {
          setPreviousCses([]);
        }
      } finally {
        finishLoading();
      }
    };

    loadForEdit();

    return () => {
      cancelled = true;
    };
  }, [cs, finishLoading, isEdit, loadPreviousCses, startLoading]);

  const handleLookup = useCallback(async () => {
    const lookupBookId = (watchedBookId || "").trim();

    if (!lookupBookId) {
      window.alert("송장번호를 입력해주세요.");
      return;
    }

    try {
      startLoading();
      const delivery = await fetchByIdOnTheFly(lookupBookId);

      if (!delivery || !delivery.bookId) {
        throw new Error("배송건을 찾을 수 없습니다.");
      }

      setVerifiedDelivery(delivery);
      setValue("bookId", delivery.bookId);
      await loadPreviousCses({
        targetBookId: delivery.bookId,
        delivery,
      });
    } catch (e) {
      setVerifiedDelivery(null);
      setPreviousCses([]);
      window.alert(`배송건 조회에 실패했습니다.\n${e.message}`);
    }

    finishLoading();
  }, [
    fetchByIdOnTheFly,
    finishLoading,
    loadPreviousCses,
    setValue,
    startLoading,
    watchedBookId,
  ]);

  const onSubmit = async (data) => {
    const payload = {
      bookId: (data.bookId || "").trim(),
      text: (data.text || "").trim(),
    };

    if (!payload.text) {
      window.alert("CS 내용을 입력해주세요.");
      return;
    }

    if (!isEdit) {
      if (!payload.bookId) {
        window.alert("송장번호를 입력해주세요.");
        return;
      }

      if (!verifiedDelivery || verifiedDelivery.bookId !== payload.bookId) {
        window.alert("송장번호를 조회한 뒤 등록해주세요.");
        return;
      }
    }

    if (isEdit && !cs.canCorpEdit()) {
      window.alert("처리가 완료된 CS는 수정할 수 없습니다.");
      return;
    }

    try {
      startLoading();

      if (isEdit) {
        await update(cs.id, { text: payload.text });
        window.alert("CS가 수정되었습니다.");
      } else {
        await create({
          bookId: verifiedDelivery.bookId,
          text: payload.text,
        });
        window.alert("CS가 등록되었습니다.");
      }

      closeModal();

      if (onAfterSubmit) {
        await onAfterSubmit();
      }
    } catch (e) {
      window.alert(
        `CS ${isEdit ? "수정" : "등록"}에 실패했습니다.\n${e.message}`,
      );
    }

    finishLoading();
  };

  return (
    <Modal>
      <Modal.Header>
        <Title>{isEdit ? "CS 수정" : "CS 등록"}</Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <FormLabel>송장번호</FormLabel>
            {isEdit ? (
              <ReadOnly>{cs.renderBookId()}</ReadOnly>
            ) : (
              <>
                <BookIdRow>
                  <Form.Control
                    name="bookId"
                    placeholder="송장번호를 입력하세요"
                    ref={register({ required: true })}
                  />
                  <LookupButton
                    type="button"
                    width="80px"
                    onClick={handleLookup}
                  >
                    조회
                  </LookupButton>
                </BookIdRow>
                {verifiedDelivery && (
                  <DeliveryInfo>
                    조회됨 · 송장번호 {verifiedDelivery.bookId}
                    {verifiedDelivery.orderIdFromCorp
                      ? ` · 주문번호 ${verifiedDelivery.orderIdFromCorp}`
                      : ""}
                  </DeliveryInfo>
                )}
              </>
            )}
            {errors.bookId && (
              <Form.Text style={{ color: "#ea2845" }}>
                송장번호를 입력해주세요.
              </Form.Text>
            )}
          </FormGroup>
          <FormGroup>
            <FormLabel>
              CS 내용<span>*</span>
            </FormLabel>
            <Form.Control
              as="textarea"
              name="text"
              placeholder="CS 내용을 입력하세요"
              disabled={!canEdit}
              ref={register({
                required: "CS 내용을 입력해주세요.",
                validate: (value) =>
                  Boolean((value || "").trim()) || "CS 내용을 입력해주세요.",
              })}
              required
              rows={4}
            />
            {errors.text && (
              <Form.Text style={{ color: "#ea2845" }}>
                {errors.text.message || "CS 내용을 입력해주세요."}
              </Form.Text>
            )}
          </FormGroup>
          {(isEdit || verifiedDelivery) && (
            <HistorySection>
              <HistoryTitle>[이전CS 내역]</HistoryTitle>
              <HistoryWrap>
                <CsHistoryList cses={previousCses} />
              </HistoryWrap>
            </HistorySection>
          )}
          <ButtonWrap>
            <ModalButton
              type="button"
              variant="secondary"
              width="100px"
              onClick={closeModal}
            >
              닫기
            </ModalButton>
            <ModalButton
              type="submit"
              width="100px"
              disabled={!canEdit || !hasText || (!isEdit && !verifiedDelivery)}
            >
              {isEdit ? "수정" : "등록"}
            </ModalButton>
          </ButtonWrap>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
